/* Native optional line-item personalization; no tracking, storage or cart interception. */
(() => {
  if (customElements.get('bp-bag-personalizer')) return;
  class BPBagPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.value = this.querySelector('[data-bp-bag-value]');
      this.input = this.querySelector('[data-bp-bag-name]');
      this.dialog = this.querySelector('dialog');
      this.openButton = this.querySelector('[data-bp-bag-open]');
      this.stage = this.querySelector('[data-bp-bag-preview]');
      this.saveButton = this.querySelector('[data-bp-bag-save]');
      if (!this.value || !this.input || !this.dialog || !this.openButton || !this.stage || !this.saveButton || typeof this.dialog.showModal !== 'function') return;
      this.ready = true;
      this.form = this.value.form;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.openButton.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-bp-bag-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        this.input.disabled = true;
        this.input.setCustomValidity('');
        this.stopAnimation();
        this.openButton.setAttribute('aria-expanded', 'false');
        if (this.isConnected) this.openButton.focus({preventScroll:true});
      }, options);
      this.dialog.addEventListener('keydown', event => {
        // Dawn quick-add closes on keyup; keep it open when this nested dialog closes.
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressEscapeKeyup = true;
        this.dialog.close();
      }, options);
      this.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.suppressEscapeKeyup) {
          event.stopPropagation();
          this.suppressEscapeKeyup = false;
        }
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      this.input.addEventListener('input', () => this.update(true), options);
      this.input.addEventListener('keydown', event => {
        if (event.key === 'Enter' && !event.isComposing) { event.preventDefault(); this.commit(); }
      }, options);
      this.saveButton.addEventListener('click', () => this.commit(), options);
      this.querySelector('[data-bp-bag-clear]').addEventListener('click', () => {
        this.input.value = ''; this.update(); this.input.focus({preventScroll:true});
      }, options);
      this.querySelectorAll('[data-bp-bag-view]').forEach(button => button.addEventListener('click', () => {
        this.stage.dataset.view = button.dataset.bpBagView;
        this.querySelectorAll('[data-bp-bag-view]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      }, options));
      this.form?.addEventListener('change', () => this.updateQuantity(), options);
      // Dawn's quantity input can sit outside the form and use its form attribute.
      this.form?.elements.namedItem('quantity')?.addEventListener('change', () => this.updateQuantity(), options);
      this.form?.addEventListener('reset', () => {
        this.value.value = ''; this.updateSummary(); this.dialog.close();
      }, options);
      this.form?.addEventListener('formdata', event => {
        const name = this.clean(this.value.value);
        if (name) event.formData.set(this.value.name, name);
        else event.formData.delete(this.value.name);
      }, options);
      this.updateSummary();
      this.openButton.hidden = false;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.stopAnimation();
      cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
      this.ready = false;
    }
    clean(value) { return value.normalize('NFC').replace(/\s+/g, ' ').trim(); }
    open() {
      if (this.dialog.open) return;
      this.suppressEscapeKeyup = false;
      if (!this.stage.firstElementChild) {
        this.stage.append(this.querySelector('[data-bp-bag-art]').content.cloneNode(true));
        document.fonts?.ready.then(() => { if (this.isConnected && this.dialog.open) this.paint(); });
      }
      this.input.value = this.value.value;
      this.input.disabled = false;
      this.dialog.showModal();
      this.openButton.setAttribute('aria-expanded', 'true');
      this.update();
    }
    commit() {
      this.input.value = this.clean(this.input.value);
      this.update();
      if (!this.input.reportValidity()) return;
      this.value.value = this.input.value;
      this.updateSummary();
      this.value.dispatchEvent(new Event('change', {bubbles:true}));
      this.dialog.close();
    }
    updateSummary() {
      const name = this.clean(this.value.value);
      this.value.disabled = !name;
      this.querySelector('[data-bp-bag-summary]').textContent = name ? `${name} · Complimentary engraving` : 'Live name preview · Optional';
      this.openButton.querySelector('.bp-bag__entry-plus').innerHTML = name ? '<svg viewBox="0 0 24 24" focusable="false"><path d="m5 12 4 4L19 6"/></svg>' : '<svg viewBox="0 0 24 24" focusable="false"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>';
    }
    updateQuantity() {
      const quantity = Number(this.form?.elements.namedItem('quantity')?.value || 1);
      this.querySelector('[data-bp-bag-quantity]').hidden = !(quantity > 1 && this.clean(this.input.value));
    }
    update(animate = false) {
      const name = this.clean(this.input.value);
      const valid = Array.from(name).length <= 24 && (!name || /^[\p{L}\p{M} .'’\-]+$/u.test(name));
      const message = valid ? '' : 'Use up to 24 characters: letters, spaces, dots, apostrophes or hyphens.';
      this.input.setCustomValidity(message);
      this.input.setAttribute('aria-invalid', String(!valid));
      this.querySelector('[data-bp-bag-error]').textContent = message;
      this.querySelector('[data-bp-bag-clear]').hidden = !this.input.value;
      this.querySelector('[data-bp-bag-count]').textContent = `${Array.from(this.input.value).length} / 24`;
      this.saveButton.firstChild.textContent = name ? 'Save engraving' : 'Continue with logo only';
      this.updateQuantity();
      this.stopAnimation();
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.paint());
      if (animate && valid && name && !matchMedia('(prefers-reduced-motion:reduce)').matches) this.scanDelay = setTimeout(() => this.animate(), 260);
    }
    paint() {
      const name = this.input.validity.valid ? this.clean(this.input.value) : '';
      this.stage.querySelectorAll('[data-bp-bag-engraving]').forEach(text => {
        text.textContent = name;
        text.style.fontSize = '44px';
        const width = text.getComputedTextLength();
        if (width > 365) text.style.fontSize = `${44 * 365 / width}px`;
      });
      this.stage.querySelector('svg')?.setAttribute('aria-label', name ? `Bag engraving preview: ${name}` : 'Bag with BlackPatel logo only');
    }
    stopAnimation() {
      clearTimeout(this.scanDelay);
      cancelAnimationFrame(this.scanFrame);
      this.stage?.querySelector('[data-bp-bag-laser]')?.setAttribute('opacity', '0');
    }
    animate() {
      if (!this.dialog.open) return;
      const text = this.stage.querySelector('[data-bp-bag-engraving]');
      const clip = this.stage.querySelector('[data-bp-bag-scan]');
      const glow = this.stage.querySelector('[data-bp-bag-glow]');
      const laser = this.stage.querySelector('[data-bp-bag-laser]');
      if (!text?.textContent || !clip || !glow || !laser) return;
      const box = text.getBBox();
      const start = performance.now();
      const step = now => {
        if (!this.dialog.open) return this.stopAnimation();
        const progress = Math.min((now - start) / 850, 1);
        const x = box.x - 20 + (box.width + 40) * progress;
        clip.setAttribute('x', x - 18);
        glow.setAttribute('cx', x);
        glow.setAttribute('cy', box.y + box.height * .58);
        laser.setAttribute('opacity', String(Math.sin(progress * Math.PI) * .8));
        if (progress < 1) this.scanFrame = requestAnimationFrame(step);
        else laser.setAttribute('opacity','0');
      };
      this.scanFrame = requestAnimationFrame(step);
    }
  }
  customElements.define('bp-bag-personalizer', BPBagPersonalizer);
})();
